import React, { createContext, useContext, useMemo, useState } from 'react';
import { useLanguage } from './LanguageContext';
import {
  OPTIONS_LANGUAGE_STORAGE_KEY,
  createOptionsTranslator,
  formatOptionsMessage,
  optionsTranslations,
} from '../lib/optionsTranslations';

const OptionsLanguageContext = createContext(null);

const resolveInitialLanguage = (globalLanguage) => {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(OPTIONS_LANGUAGE_STORAGE_KEY);
    if (stored && optionsTranslations[stored]) {
      return stored;
    }
  }

  if (globalLanguage === 'ptBR') {
    return 'pt';
  }

  return 'en';
};

export const OptionsLanguageProvider = ({ children }) => {
  const { language: globalLanguage } = useLanguage();
  const [language, setLanguageState] = useState(() => resolveInitialLanguage(globalLanguage));

  const setLanguage = (nextLanguage) => {
    if (!optionsTranslations[nextLanguage]) return;
    setLanguageState(nextLanguage);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(OPTIONS_LANGUAGE_STORAGE_KEY, nextLanguage);
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'pt' : 'en');
  };

  const t = useMemo(() => createOptionsTranslator(language), [language]);
  const value = useMemo(() => ({
    language,
    setLanguage,
    toggleLanguage,
    t,
    formatMessage: formatOptionsMessage,
    isPortuguese: language === 'pt',
    isEnglish: language === 'en',
  }), [language, t]);

  return <OptionsLanguageContext.Provider value={value}>{children}</OptionsLanguageContext.Provider>;
};

export const useOptionsLocale = () => {
  const context = useContext(OptionsLanguageContext);
  if (!context) {
    throw new Error('useOptionsLocale must be used within OptionsLanguageProvider');
  }
  return context;
};

export default OptionsLanguageContext;